/**
 * User Exercise Preference Validation Schemas
 *
 * Zod schemas for validating user exercise preference-related forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Create user exercise preference validation
 * - Update user exercise preference validation
 * - Tracking type validation using exercise type options
 */

import { z } from './zod';
import {
  getExerciseTypeOptions,
  createEnumFromOptions,
} from '../utils/select-options';

// =============================================================================
// COMMON VALIDATION RULES
// =============================================================================

/**
 * Tracking type validation
 */
const trackingTypeSchema = createEnumFromOptions(getExerciseTypeOptions());

// =============================================================================
// USER EXERCISE PREFERENCE VALIDATION SCHEMAS
// =============================================================================

/**
 * Create user exercise preference form validation schema
 */
export const createSchema = z.object({
  exercise_guid: z.string().min(1, 'Exercise GUID is required'),
  tracking_type: trackingTypeSchema,
});

/**
 * Update user exercise preference form validation schema
 */
export const updateSchema = z.object({
  tracking_type: trackingTypeSchema,
});

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type CreateUserExercisePreferenceInput = z.input<typeof createSchema>;
export type CreateUserExercisePreferenceOutput = z.output<typeof createSchema>;

export type UpdateUserExercisePreferenceInput = z.input<typeof updateSchema>;
export type UpdateUserExercisePreferenceOutput = z.output<typeof updateSchema>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate create user exercise preference data
 * @param data - The data to validate
 * @returns The validated and typed user exercise preference
 * @throws ZodError if validation fails
 */
export function validateCreateUserExercisePreference(
  data: unknown
): CreateUserExercisePreferenceOutput {
  return createSchema.parse(data);
}

/**
 * Safely validate create user exercise preference data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateCreateUserExercisePreference(data: unknown) {
  return createSchema.safeParse(data);
}

/**
 * Check if data is a valid create user exercise preference request
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidCreateUserExercisePreference(data: unknown): boolean {
  return createSchema.safeParse(data).success;
}

/**
 * Validate update user exercise preference data
 * @param data - The data to validate
 * @returns The validated and typed user exercise preference update
 * @throws ZodError if validation fails
 */
export function validateUpdateUserExercisePreference(
  data: unknown
): UpdateUserExercisePreferenceOutput {
  return updateSchema.parse(data);
}

/**
 * Safely validate update user exercise preference data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateUserExercisePreference(data: unknown) {
  return updateSchema.safeParse(data);
}

/**
 * Check if data is a valid update user exercise preference request
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateUserExercisePreference(data: unknown): boolean {
  return updateSchema.safeParse(data).success;
}
